import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { useDesignContext } from '../../hooks/useDesignContext';
import { buildApiUrl } from '../../config/api';

import { persistTextToCache } from './Text';



// Styled Components
const Breadcrumbs = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  margin-bottom: 10px;
  color: #22334d;
  font-family: 'Inter', 'Segoe UI', Arial, sans-serif;
  flex-wrap: wrap;
  user-select: none;
  
  .crumb {
    cursor: pointer;
    color: #5D722C;
    font-weight: 500;
    
    &:hover { 
      text-decoration: underline; 
    }
  }
  
  .sep {
    color: #b0b7c3;
    margin: 0 2px;
  }
  
  .current {
    color: #22334d;
    font-weight: 600;
    cursor: default;
  }
`;

const FormGroup = styled.div`
  font-family: 'Inter', 'Segoe UI', Arial, sans-serif;
  margin-bottom: 20px;
`;

const Label = styled.label`
  font-family: 'Inter', 'Segoe UI', Arial, sans-serif;
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #34495e;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Input = styled.input`
  font-family: 'Inter', 'Segoe UI', Arial, sans-serif;
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e1e5e9;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
  background: #fafbfc;
  
  &:focus {
    outline: none;
    border-color: #5D722C;
    box-shadow: 0 0 0 3px rgba(93, 114, 44, 0.1);
    background: #ffffff;
  }
  
  &::placeholder {
    color: #95a5a6;
    font-weight: 400;
  }
`;

const AnimatedOkButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  border: none;
  border-radius: 10px;
  padding: 10px 16px;
  background: #5D722C;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;

  &:hover {
    background: #4a5b23;
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(93, 114, 44, 0.35);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 3px 8px rgba(93, 114, 44, 0.28);
  }

  &:disabled {
    background: #95a5a6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const LoadingMessage = styled.div<{ $color?: string }>`
  font-size: 13px;
  color: ${props => props.$color || '#5D722C'};
  text-align: center;
  padding: 8px;
`;

const ModelButton = styled.button<{ $isSelected?: boolean }>`
  border: 1px solid #e1e5e9;
  border-radius: 8px;
  padding: 5px;
  cursor: pointer;
  background: #fff;
  transition: all 0.2s ease;
  
  ${props => props.$isSelected && `
    border-color: #5D722C;
    box-shadow: 0 0 0 2px rgba(93, 114, 44, 0.2);
  `}

  &:hover {
    border-color: #5D722C;
    transform: translateY(-1px);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const SizeButton = styled.button<{ $isSelected?: boolean }>`
  padding: 12px 18px;
  border: ${props => props.$isSelected ? '2px solid #5D722C' : '1px solid #e2e8f0'};
  border-radius: 10px;
  background: ${props => props.$isSelected ? '#f0f7e6' : '#fff'};
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.$isSelected ? '#3f4f1e' : '#334155'};
  min-width: 90px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: ${props => props.$isSelected ? '0 4px 6px -1px rgba(93, 114, 44, 0.1), 0 2px 4px -1px rgba(93, 114, 44, 0.06)' : '0 1px 3px rgba(0,0,0,0.05)'};

  &:hover {
    border-color: #5D722C;
    transform: translateY(-2px);
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
    background: #f8fafc;
    border-color: #e2e8f0;
    color: #94a3b8;
  }
`;

const PreviewContainer = styled.div`
  min-height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f9fa;
  border: 1px solid #e1e5e9;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
`;

const PreviewImage = styled.img`
  max-width: 100%;
  max-height: 240px;
  object-fit: contain;
`;

const ModelsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const LocalSpinner = styled.div<{ $size?: number }>`
  width: ${props => props.$size || 24}px;
  height: ${props => props.$size || 24}px;
  border: 3px solid rgba(93, 114, 44, 0.2);
  border-top-color: #5D722C;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

const CenterLoader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  width: 100%;
  color: #5D722C;
  font-size: 13px;
  font-weight: 500;
  gap: 12px;
`;


const StepContainer = styled.div<{ $isVisible: boolean }>`
  display: ${props => props.$isVisible ? 'block' : 'none'};
`;

const ModelThumbnail = React.memo(({ src, alt }: { src: string; alt: string }) => (
  <img
    src={src}
    alt={alt}
    loading="eager"
    decoding="async"
    style={{
      width: '100%',
      height: '100%',
      objectFit: 'contain'
    }}
    onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
      e.currentTarget.style.display = 'none';
    }}
  />
));
ModelThumbnail.displayName = 'ModelThumbnail';



const ConfirmationModal = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.36);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 4200;
`;

const ModalContent = styled.div`
  width: 480px;
  background: #fff;
  border-radius: 12px;
  padding: 18px;
  box-shadow: 0 20px 60px rgba(2, 6, 23, 0.35);
`;

const ModalIcon = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 10px;
  background: #f0f7e6;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalTitle = styled.h4`
  margin: 0;
  color: #0f172a;
`;

const ModalText = styled.p`
  margin-top: 8px;
  color: #475569;
  font-size: 13px;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 16px;
`;

const CancelButton = styled.button`
  padding: 8px 12px;
  border-radius: 8px;
`;

const ConfirmButton = styled.button`
  padding: 8px 12px;
  border-radius: 8px;
  border: none;
  background: #5D722C;
  color: #fff;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover {
    background: #4a5b23;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

// Compatibility Modal Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const CompatibilityModalCard = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow: hidden;
`;

const ModalHeader = styled.div`
  padding: 16px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 600;
  font-size: 16px;
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  color: #fff;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalBody = styled.div`
  padding: 20px;
`;

const CompatibilityModalContent = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-start;
`;

const CompatibilityIcon = styled.div`
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  background: #f3f4f6;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CompatibilityText = styled.div`
  flex: 1;
`;

const CompatibilityTitle = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 8px;
`;

const CompatibilityDescription = styled.div`
  color: #6b7280;
  line-height: 1.5;
  margin-bottom: 20px;
`;

const CompatibilityActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const ActionButton = styled.button<{ $primary?: boolean }>`
  padding: 7px 10px;
  border: 1.5px solid ${props => props.$primary ? '#4a5b23' : '#b8cc8a'};
  border-radius: 6px;
  background: ${props => props.$primary ? '#5D722C' : '#eaf4d6'};
  color: ${props => props.$primary ? 'white' : '#2f4a1c'};
  cursor: pointer;
  font-weight: 500;
  font-size: 12px;
  font-family: 'Inter', 'Segoe UI', Arial, sans-serif;
  transition: all 0.12s;

  &:hover {
    background: ${props => props.$primary ? '#4a5b23' : '#dcebbd'};
    border-color: ${props => props.$primary ? '#4a5b23' : '#9fbc69'};
  }
`;

// Constants

const normalizeImagePath = (path: string | null | undefined): string => {
  if (!path) return '';
  return String(path).replace(/^\/?uploads\//, '');
};

// Types
type ModelOption = {
  id: number;
  name: string;
  model_no?: string;
  preview_image?: string;
  is_active?: number;
};

type SizeOption = {
  id: number;
  die_size: string;
  base_size: string;
  size_type: string;
  preview_image?: string;
  is_active?: number;
  porcelain_width_in?: number | null;
  porcelain_height_in?: number | null;
  porcelain_shape?: string | null;
};

export interface ShapesProps {
  onClose: () => void;
  onShapeSelect: (modelId: number, sizeName?: string, sizeId?: number) => void;

  selectedColorId: number | null;
  forcedGranite: string | null;
  incrementTemplateResetKey: () => void;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  autoSelectFirstShape?: boolean;
  initialModels?: ModelOption[];
}

const Shapes: React.FC<ShapesProps> = ({
  onClose,
  onShapeSelect,
  selectedColorId,
  forcedGranite,
  incrementTemplateResetKey,
  setIsLoading,
  autoSelectFirstShape = false,
  initialModels = [],
}) => {

  const {
    currentDesign,
    setTemplateElement,
    setSelectedGraniteMeta,
    setSelectedGraniteImage,
    updateDesignNoHistory,
    selectedGraniteMeta,
    setSelectedGraniteBaseImage,
    setIsApplyingDesign,
    setLoadingMessage,
    sharedModels,
    isGlobalLoading,
    fetchModelDetails,
    fetchDesignPreview,
    loadShapesData,
    setSelectedBackground,
    setActiveSide,
    setShowPersonalTextOnCanvas,
    designMode,
  } = useDesignContext();

  // State
  const [models, setModels] = useState<ModelOption[]>(sharedModels.length > 0 ? sharedModels : initialModels);
  const [selectedModel, setSelectedModel] = useState<ModelOption | null>(null);
  const [sizes, setSizes] = useState<SizeOption[]>([]);
  const [selectedSizeIndex, setSelectedSizeIndex] = useState<number>(-1);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState<boolean>(false);
  const [sizesLoading, setSizesLoading] = useState<boolean>(false);
  // Show spinner whenever models haven't arrived yet, regardless of global loading state.
  // Previously used `isGlobalLoading && sharedModels.length === 0` which evaluated to
  // false (isGlobalLoading is already done by the time the modal opens) and caused
  // "No models available" to appear instead of a loading spinner.
  const [modelsLoading, setModelsLoading] = useState<boolean>(sharedModels.length === 0 && initialModels.length === 0);

  // Sync models from shared context
  useEffect(() => {
    if (sharedModels.length > 0) {
      setModels(sharedModels);
      setModelsLoading(false);
    }
  }, [sharedModels]);
  const preloadedPreviewRef = useRef<boolean>(false);
  const preserveSizeSelectionRef = useRef<boolean>(false);
  const lastModelIdRef = useRef<number | null>(null);
  const lastColorIdRef = useRef<number | null>(null);
  const [skipArtworkCheck, setSkipArtworkCheck] = useState<boolean>(false);
  const [imageLoading, setImageLoading] = useState<boolean>(false);

  // Preloaders should hide immediately when data is ready
  const MODELS_MIN_VISIBLE_MS = 0; // milliseconds
  const PREVIEW_MIN_VISIBLE_MS = 0; // milliseconds
  const modelsLoadingStartRef = useRef<number | null>(null);
  const modelsLoadingTimeoutRef = useRef<any>(null);
  const previewLoadingStartRef = useRef<number | null>(null);
  const previewLoadingTimeoutRef = useRef<any>(null);

  const startModelsLoading = useCallback(() => {
    // clear any pending hide timeout
    if (modelsLoadingTimeoutRef.current) {
      clearTimeout(modelsLoadingTimeoutRef.current);
      modelsLoadingTimeoutRef.current = null;
    }
    modelsLoadingStartRef.current = Date.now();
    setModelsLoading(true);
  }, []);

  const stopModelsLoading = useCallback(() => {
    const start = modelsLoadingStartRef.current || Date.now();
    const elapsed = Date.now() - start;
    const remaining = MODELS_MIN_VISIBLE_MS - elapsed;
    if (remaining > 0) {
      modelsLoadingTimeoutRef.current = setTimeout(() => {
        setModelsLoading(false);
        modelsLoadingTimeoutRef.current = null;
      }, remaining);
    } else {
      setModelsLoading(false);
    }
  }, []);

  const startPreviewLoading = useCallback(() => {
    if (previewLoadingTimeoutRef.current) {
      clearTimeout(previewLoadingTimeoutRef.current);
      previewLoadingTimeoutRef.current = null;
    }
    previewLoadingStartRef.current = Date.now();
    setPreviewLoading(true);
  }, []);

  const stopPreviewLoading = useCallback(() => {
    const start = previewLoadingStartRef.current || Date.now();
    const elapsed = Date.now() - start;
    const remaining = PREVIEW_MIN_VISIBLE_MS - elapsed;
    if (remaining > 0) {
      previewLoadingTimeoutRef.current = setTimeout(() => {
        setPreviewLoading(false);
        previewLoadingTimeoutRef.current = null;
      }, remaining);
    } else {
      setPreviewLoading(false);
    }
  }, []);

  const [step, setStep] = useState<'shape' | 'size'>('shape');


  // Breadcrumbs logic
  const breadcrumbs = useMemo(() => {
    const crumbs: { label: string; onClick?: () => void; isCurrent?: boolean }[] = [
      {
        label: 'Select Model',
        onClick: () => {
          setSelectedModel(null);
          setStep('shape');
        },
        isCurrent: !selectedModel
      }
    ];

    if (selectedModel) {
      crumbs.push({
        label: selectedModel.name,
        isCurrent: true
      });
    }

    return crumbs;
  }, [selectedModel]);

  // Global cache for shapes data to avoid redundant fetches
  const shapesCache = useRef<Record<string, any>>({}).current;

  // Global fetchModels removed - now handled by DesignProvider sharedModels

  const fetchSizes = useCallback(async (model: ModelOption) => {
    if (!model) return;

    const cacheKey = `sizes_${model.id}_${selectedColorId || 'any'}`;
    if (shapesCache[cacheKey]) {
      const cachedSizes = shapesCache[cacheKey];
      setSizes(cachedSizes);
      // Restore previously selected size from current design (same logic as non-cache path)
      let nextIndex = -1;
      if (currentDesign) {
        const currentSizeId = (currentDesign as any).selectedSizeId;
        if (currentSizeId) {
          const foundIndex = cachedSizes.findIndex((s: any) => Number(s.id) === Number(currentSizeId));
          if (foundIndex >= 0) nextIndex = foundIndex;
        }
      }
      setSelectedSizeIndex(nextIndex);
      return;
    }

    try {
      setSizesLoading(true);
      startPreviewLoading();

      const data = await fetchModelDetails(model.id, selectedColorId || undefined);

      if (Array.isArray(data.sizes)) {
        setSizes(data.sizes);
        shapesCache[cacheKey] = data.sizes;
        // Ensure size index is valid and prefer an active size when color_id is provided
        if (data.sizes.length > 0) {
          // Check against current design to auto-select if matching
          let nextIndex = -1;

          if (currentDesign) {
            const currentSizeId = (currentDesign as any).selectedSizeId;
            if (currentSizeId) {
              const foundIndex = data.sizes.findIndex((s: any) => Number(s.id) === Number(currentSizeId));
              if (foundIndex >= 0) {
                nextIndex = foundIndex;
              }
            }
          }

          // If we found a matching size from current design, use it.
          // Otherwise, stick with -1 (no selection) to force user to choose.
          setSelectedSizeIndex(nextIndex);
        } else {
          setSizes([]);
          setSelectedSizeIndex(-1);
        }
      } else {
        setSizes([]);
        setSelectedSizeIndex(-1);
      }
    } catch (error) {
      setSizes([]);
      setSelectedSizeIndex(-1);
    } finally {
      setSizesLoading(false);
      stopPreviewLoading();
    }
  }, [selectedColorId, currentDesign]);

  const fetchPreview = useCallback(async (model: ModelOption, sizeIndex: number) => {
    if (!model || !sizes.length || sizeIndex < 0 || sizeIndex >= sizes.length) return;

    const sizeObj = sizes[sizeIndex];
    const cacheKey = `preview_${model.id}_${sizeObj.id}_${selectedColorId || 'any'}`;

    if (shapesCache[cacheKey]) {
      setPreviewUrl(shapesCache[cacheKey]);
      setImageLoading(true);
      return;
    }

    setPreviewUrl(null);
    startPreviewLoading();

    try {
      // Prefer model_img from API (include color_id if available)
      const sizeId = sizeObj.id;
      let preferredUrl: string | null = null;
      try {
        // Use shared fetchDesignPreview for caching and deduplication
        const colorId = selectedColorId || 12; // Default color if not set
        const data = await fetchDesignPreview(model.id, sizeId, colorId);

        if (data.status === 'success' && (data.data?.model_img || data.data?.preview_png)) {
          if (data.data.model_img_api_url && data.data.model_img_api_url !== buildApiUrl('/api/uploads/')) {
            preferredUrl = data.data.model_img_api_url;
          } else if (data.data.model_img) {
            const normalizedModel = normalizeImagePath(data.data.model_img);
            preferredUrl = buildApiUrl(`/api/uploads/${normalizedModel}`);
          } else if (data.data.preview_api_url) {
            preferredUrl = data.data.preview_api_url;
          } else if (data.data.preview_png) {
            const normalizedPath = normalizeImagePath(data.data.preview_png);
            preferredUrl = buildApiUrl(`/api/uploads/${normalizedPath}`);
          }
        }
      } catch (e) {
        // ignore and fall back to sizeObj.preview_image
      }

      // Fallback to preview_image from size if API did not give us a preferred URL
      if (!preferredUrl && sizeObj?.preview_image) {
        const normalizedPath = normalizeImagePath(sizeObj.preview_image);
        preferredUrl = buildApiUrl(`/api/uploads/${normalizedPath}`);
      }

      setPreviewUrl(preferredUrl || null);
      if (preferredUrl) {
        shapesCache[cacheKey] = preferredUrl;
        setImageLoading(true);
      }
    } catch (error) {
      setPreviewUrl(null);
    } finally {
      stopPreviewLoading();
    }
  }, [sizes, selectedColorId, fetchDesignPreview]);

  // After user confirms selection, set/clear the base for the chosen model/size (+ current color) only then
  const applyBaseForSelection = useCallback(async (modelId: number, sizeId: number, colorId?: number | null) => {
    try {
      // Use shared fetchDesignPreview for caching and deduplication
      const effectiveColorId = colorId || 12; // Default color if not set
      const d = await fetchDesignPreview(modelId, sizeId, effectiveColorId);
      const baseApi = d?.data?.preview_img_api_url;
      const baseRaw = d?.data?.preview_img;
      const baseUrl = baseApi && baseApi !== buildApiUrl('/api/uploads/')
        ? baseApi
        : (baseRaw ? buildApiUrl(`/api/uploads/${String(baseRaw).replace(/^\/*uploads\//, '')}`) : null);
      if (setSelectedGraniteBaseImage) setSelectedGraniteBaseImage(baseUrl || null);
    } catch {
      if (setSelectedGraniteBaseImage) setSelectedGraniteBaseImage(null);
    }
  }, [setSelectedGraniteBaseImage, fetchDesignPreview]);

  // Effects
  // fetchModels call removed - now handled by DesignProvider sharedModels
  // useEffect(() => {
  //   fetchModels();
  // }, [fetchModels]);

  // Cleanup any pending timeouts when component unmounts
  useEffect(() => {
    return () => {
      if (modelsLoadingTimeoutRef.current) {
        clearTimeout(modelsLoadingTimeoutRef.current);
        modelsLoadingTimeoutRef.current = null;
      }
      if (previewLoadingTimeoutRef.current) {
        clearTimeout(previewLoadingTimeoutRef.current);
        previewLoadingTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (selectedModel) {
      fetchSizes(selectedModel);
    }
  }, [selectedModel, fetchSizes]);

  useEffect(() => {
    if (selectedModel && sizes.length > 0) {
      if (preloadedPreviewRef.current) {
        // Skip once after preloading inside fetchSizes
        preloadedPreviewRef.current = false;
        return;
      }
      setPreviewUrl(null); // Clear previous image immediately when selection changes
      fetchPreview(selectedModel, selectedSizeIndex);
    }
  }, [selectedModel, sizes, selectedSizeIndex, fetchPreview]);

  useEffect(() => {
    setSelectedModel(null);
    setSizes([]);
    setSelectedSizeIndex(-1);
    setPreviewUrl(null);
    setStep('shape');
  }, [forcedGranite]);

  // Memoized computations
  const { singles, doubles } = useMemo(() => {
    const singles = sizes.filter(s =>
      String(s.size_type || '').toLowerCase() === 'single'
    );
    const doubles = sizes.filter(s =>
      String(s.size_type || '').toLowerCase() === 'double'
    );
    return { singles, doubles };
  }, [sizes]);

  const isSizeSelectDisabled = useMemo(() => {
    const s: any = sizes[selectedSizeIndex];
    const inactive = Boolean(selectedColorId && s && typeof s.is_active !== 'undefined' && Number(s.is_active) === 0);
    return !selectedModel || !s || inactive;
  }, [selectedModel, sizes, selectedSizeIndex, selectedColorId]);

  // Render methods
  const renderModelSection = () => (
    <FormGroup>
      <Label>Select Model</Label>

      {models.length === 0 ? (
        null
      ) : (
        <ModelsGrid>
          {models.map((model) => {
            const disabledByModel = typeof model.is_active === 'number' ? Number(model.is_active) === 0 : false;
            const isDisabled = disabledByModel;
            return (
              <ModelButton
                key={model.id}
                disabled={isDisabled}

                onClick={() => {
                  if (isDisabled) return;
                  setSelectedModel(model);
                  setPreviewUrl(null);
                  setSelectedSizeIndex(-1); // Don't auto-select first size
                  setStep('size');
                }}
              >
                <>
                  <div style={{
                    width: '100%',
                    height: 72,
                    background: '#fafbfc',
                    padding: 6,
                    border: '1px solid #e1e5e9',
                    borderRadius: 6,
                    marginBottom: 6,
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative'
                  }}>
                    {model.preview_image && (
                      <ModelThumbnail
                        src={buildApiUrl(`/api/uploads/${normalizeImagePath(model.preview_image)}`)}
                        alt={model.name}
                      />
                    )}
                  </div>
                  <div style={{
                    fontSize: 12,
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    textAlign: 'center'
                  }}>
                    {model.name}
                  </div>
                </>
              </ModelButton>
            );
          })}
        </ModelsGrid>
      )}
    </FormGroup>
  );

  const renderSizeSection = () => {
    if (!sizes.length) {
      return (
        null
      );
    }

    return (
      <FormGroup>
        <Label>Size</Label>
        <div style={{ display: 'grid', gap: 10 }}>
          {singles.length > 0 && (
            <div>
              <div style={{
                fontSize: 12,
                fontWeight: 700,
                marginBottom: 6,
                color: '#0c4a6e'
              }}>
                SINGLE
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {singles.map((size) => {
                  const globalIndex = sizes.findIndex(s => Number(s?.id) === Number((size as any)?.id));
                  const isDisabled = Boolean(selectedColorId && typeof (size as any)?.is_active !== 'undefined' && Number((size as any).is_active) === 0);
                  return (
                    <SizeButton
                      key={`single-${size.id}`}
                      $isSelected={selectedSizeIndex === globalIndex}
                      disabled={isDisabled}
                      onClick={() => {
                        if (isDisabled) return;
                        // Ignore click if this model+size is already active
                        if (
                          selectedModel &&
                          Number(selectedModel.id) === Number((currentDesign as any)?.selectedModelId) &&
                          Number((size as any)?.id) === Number((currentDesign as any)?.selectedSizeId)
                        ) return;
                        if (globalIndex >= 0) {
                          setPreviewUrl(null);
                          preserveSizeSelectionRef.current = true;
                          setSelectedSizeIndex(globalIndex);
                          handleSizeSelect(globalIndex);
                        }
                      }}
                    >
                      {size.die_size || '—'}
                    </SizeButton>
                  );
                })}
              </div>
            </div>
          )}

          {doubles.length > 0 && (
            <div>
              <div style={{
                fontSize: 12,
                fontWeight: 700,
                marginBottom: 6,
                color: '#0c4a6e'
              }}>
                DOUBLE
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {doubles.map((size) => {
                  const globalIndex = sizes.findIndex(s => Number(s?.id) === Number((size as any)?.id));
                  const isDisabled = Boolean(selectedColorId && typeof (size as any)?.is_active !== 'undefined' && Number((size as any).is_active) === 0);
                  return (
                    <SizeButton
                      key={`double-${size.id}`}
                      $isSelected={selectedSizeIndex === globalIndex}
                      disabled={isDisabled}
                      onClick={() => {
                        if (isDisabled) return;
                        // Ignore click if this model+size is already active
                        if (
                          selectedModel &&
                          Number(selectedModel.id) === Number((currentDesign as any)?.selectedModelId) &&
                          Number((size as any)?.id) === Number((currentDesign as any)?.selectedSizeId)
                        ) return;
                        if (globalIndex >= 0) {
                          setPreviewUrl(null);
                          preserveSizeSelectionRef.current = true;
                          setSelectedSizeIndex(globalIndex);
                          handleSizeSelect(globalIndex);
                        }
                      }}
                    >
                      {size.die_size || '—'}
                    </SizeButton>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </FormGroup>
    );
  };

  const renderPreviewSection = () => (
    <FormGroup>
      <Label>Preview</Label>
      <PreviewContainer>
        null
      </PreviewContainer>
    </FormGroup>
  );

  // Event handlers
  const handleFallbackTemplate = useCallback((colorId: number, modelId: number) => {


    // Remove template elements
    if (updateDesignNoHistory) {
      updateDesignNoHistory((prev: any) => {
        // Persist text to cache before removal
        persistTextToCache(prev.elements, prev.id);

        return {
          ...prev,
          elements: prev.elements.filter(
            (el: any) =>
              !(el.type === 'artwork' && (el.properties?.role === 'template' || el.properties?.isTemplate)) &&
              el.type !== 'text'
          )
        };
      });
    }

    if (setSelectedGraniteMeta) {
      setSelectedGraniteMeta(null);
    }



    onShapeSelect(modelId);
    try { if (designMode !== 'ESQ') setShowPersonalTextOnCanvas(false); } catch { }
    setIsLoading(false);
    onClose();
  }, [updateDesignNoHistory, setSelectedGraniteMeta, setSelectedGraniteImage, onShapeSelect, setIsLoading, onClose, setShowPersonalTextOnCanvas]);

  // Version of handleSizeSelect that skips artwork check (used after confirmation)
  const handleSizeSelectWithSkip = useCallback(async () => {
    if (!selectedModel || !sizes[selectedSizeIndex]) {
      setIsLoading(false);
      return;
    }
    // Bail out if the same model+size is already active — avoids clearing the canvas
    const currentSize = sizes[selectedSizeIndex];
    if (
      Number(selectedModel.id) === Number((currentDesign as any)?.selectedModelId) &&
      Number((currentSize as any)?.id) === Number((currentDesign as any)?.selectedSizeId)
    ) {
      setIsLoading(false);
      onClose();
      return;
    }

    if (selectedColorId) {
      const sel: any = sizes[selectedSizeIndex];
      if (sel && typeof sel.is_active !== 'undefined' && Number(sel.is_active) === 0) {
        setIsLoading(false);
        return;
      }
    }

    setIsLoading(true);
    setLoadingMessage('Loading shape...');

    const colorId = selectedColorId ?? 12; // Default to grey if not set
    const selectedSize = sizes[selectedSizeIndex];

    // Clear current assets immediately for a fresh reload
    if (setSelectedGraniteImage) setSelectedGraniteImage(null);
    if (setSelectedGraniteBaseImage) setSelectedGraniteBaseImage(null);

    // Batch model ID + element cleanup into a single state update.
    // Clear ALL elements so the new model starts with a completely fresh canvas.
    if (updateDesignNoHistory) {
      updateDesignNoHistory((prev: any) => ({
        ...prev,
        selectedModelId: selectedModel.id,
        selectedModelName: selectedModel.name,
        elements: []
      }));
    }

    // Determine image source
    let imgSrc = previewUrl;
    if (selectedSize.preview_image) {
      imgSrc = buildApiUrl(`/api/uploads/${normalizeImagePath(selectedSize.preview_image)}`);
    }

    if (imgSrc) {
      incrementTemplateResetKey();

      // Try to parse dimensions directly from the filename (e.g. pjb_36x24.png → 36×24).
      // If that succeeds we skip the network round-trip entirely.
      const parseSizeFromPath = (p?: string | null): { w: number; h: number } | null => {
        if (!p) return null;
        try {
          const part = String(p).split('/').pop() || String(p);
          const m = part.match(/([_-]?)(\d{1,4})[xX](\d{1,4})(?=\.|$)/);
          if (m) { const w = Number(m[2]), h = Number(m[3]); if (w > 0 && h > 0) return { w, h }; }
        } catch { }
        return null;
      };

      const applyDimensions = async (useW: number, useH: number) => {
        const targetName = (selectedModel?.name || '').toString().toLowerCase();
        const isMF01 = targetName.includes('mf01') || targetName.includes('flat marker');

        if (setTemplateElement) {
          setTemplateElement({
            src: imgSrc!,
            width: useW,
            height: useH,
            colorId: colorId,
            isFlatMarker: Boolean(isMF01)
          });
        }

        if (setSelectedGraniteMeta) {
          setSelectedGraniteMeta({
            modelId: selectedModel.id,
            sizeId: selectedSize.id,
            colorId: colorId,
            src: imgSrc!
          });
        }

        const sizeName = selectedSize?.base_size
          ? `${selectedSize.die_size} / ${selectedSize.base_size}`
          : String(selectedSize?.die_size || selectedSize?.size_type || selectedSize?.id || '');

        // Single batched update so downstream effects fire exactly once
        if (updateDesignNoHistory) {
          updateDesignNoHistory(prev => ({
            ...prev,
            selectedModelId: selectedModel.id,
            selectedModelName: selectedModel.name,
            selectedSizeId: selectedSize.id,
            selectedSizeName: sizeName,
            porcelain_width_in: selectedSize.porcelain_width_in ?? prev.porcelain_width_in,
            porcelain_height_in: selectedSize.porcelain_height_in ?? prev.porcelain_height_in,
            porcelain_shape: selectedSize.porcelain_shape ?? prev.porcelain_shape,
          }));
        }

        if (setSelectedGraniteImage) {
          setSelectedGraniteImage(null);
        }

        // Now that size is confirmed, set/clear base strictly for this selection
        try { await applyBaseForSelection(selectedModel.id, selectedSize.id, selectedColorId ?? null); } catch { }
        onShapeSelect(selectedModel.id, sizeName, selectedSize.id);
        try { if (designMode !== 'ESQ') setShowPersonalTextOnCanvas(false); } catch { }
        setIsLoading(false);

        onClose();
      };

      const logical = parseSizeFromPath(selectedSize.preview_image || imgSrc);
      if (logical) {
        // Dimensions encoded in filename — no image download needed here.
        // Designer.tsx will load the image for actual rendering.
        applyDimensions(logical.w, logical.h);
      } else {
        // Fallback: load image to read natural pixel dimensions
        const img = new Image();
        img.onload = () => { applyDimensions(img.naturalWidth || img.width, img.naturalHeight || img.height); };
        img.onerror = () => { handleFallbackTemplate(colorId, selectedModel.id); };
        img.src = imgSrc;
      }
    } else {
      // No image available, use fallback
      handleFallbackTemplate(colorId, selectedModel.id);
    }
  }, [
    selectedModel,
    sizes,
    selectedSizeIndex,
    previewUrl,
    selectedColorId,
    setIsLoading,
    setLoadingMessage,
    incrementTemplateResetKey,
    setTemplateElement,
    setSelectedGraniteMeta,
    setSelectedGraniteImage,
    updateDesignNoHistory,
    onShapeSelect,
    onClose,
    handleFallbackTemplate,
    setIsApplyingDesign,
    applyBaseForSelection
  ]);

  const handleSizeSelect = useCallback(async (overrideIndex?: number) => {
    const activeIndex = typeof overrideIndex === 'number' ? overrideIndex : selectedSizeIndex;

    if (!selectedModel || !sizes[activeIndex]) {
      setIsLoading(false);
      return;
    }
    // Bail out if the same model+size is already active — avoids clearing the canvas
    const currentSize = sizes[activeIndex];
    if (
      Number(selectedModel.id) === Number((currentDesign as any)?.selectedModelId) &&
      Number((currentSize as any)?.id) === Number((currentDesign as any)?.selectedSizeId)
    ) {
      setIsLoading(false);
      onClose();
      return;
    }

    if (selectedColorId) {
      const sel: any = sizes[activeIndex];
      if (sel && typeof sel.is_active !== 'undefined' && Number(sel.is_active) === 0) {
        setIsLoading(false);
        return;
      }
    }

    // Compatibility checks removed as per user request


    if (skipArtworkCheck) {
      setSkipArtworkCheck(false);
    }

    setIsLoading(true);
    setLoadingMessage('Loading shape...');

    const colorId = selectedColorId ?? 12; // Default to grey if not set
    const selectedSize = sizes[activeIndex];

    // Clear current assets immediately for a fresh reload
    if (setSelectedGraniteImage) setSelectedGraniteImage(null);
    if (setSelectedGraniteBaseImage) setSelectedGraniteBaseImage(null);
    if (setSelectedBackground) setSelectedBackground(null);

    // Batch model ID + element cleanup into a single state update to avoid
    // triggering downstream effects (availability, backgrounds) with a
    // stale/mismatched sizeId before the real sizeId is set later.
    // Also reset to front side so "Flip to Back" state doesn't carry over to new model.
    // Clear ALL elements so the new model starts with a completely fresh canvas.
    if (updateDesignNoHistory) {
      updateDesignNoHistory((prev: any) => ({
        ...prev,
        selectedModelId: selectedModel.id,
        selectedModelName: selectedModel.name,
        activeSide: 'front',
        elements: []
      }));
    }
    // Also reset the context activeSide so the toolbar reflects the change immediately.
    setActiveSide('front');

    let imgSrc = previewUrl;
    if (selectedSize.preview_image) {
      imgSrc = buildApiUrl(`/api/uploads/${normalizeImagePath(selectedSize.preview_image)}`);
    }

    if (imgSrc) {
      incrementTemplateResetKey();

      const parseSizeFromPath = (p?: string | null): { w: number; h: number } | null => {
        if (!p) return null;
        try {
          const part = String(p).split('/').pop() || String(p);
          const m = part.match(/([_-]?)(\d{1,4})[xX](\d{1,4})(?=\.|$)/);
          if (m) { const w = Number(m[2]), h = Number(m[3]); if (w > 0 && h > 0) return { w, h }; }
        } catch { }
        return null;
      };

      const applyDimensions = async (useW: number, useH: number) => {
        const targetName = (selectedModel?.name || '').toString().toLowerCase();
        const isMF01 = targetName.includes('mf01') || targetName.includes('flat marker');

        if (setTemplateElement) {
          setTemplateElement({
            src: imgSrc!,
            width: useW,
            height: useH,
            colorId: colorId,
            isFlatMarker: Boolean(isMF01)
          });
        }

        if (setSelectedGraniteMeta) {
          setSelectedGraniteMeta({
            modelId: selectedModel.id,
            sizeId: selectedSize.id,
            colorId: colorId,
            src: imgSrc!
          });
        }

        const sizeName = selectedSize?.base_size
          ? `${selectedSize.die_size} / ${selectedSize.base_size}`
          : String(selectedSize?.die_size || selectedSize?.size_type || selectedSize?.id || '');

        // Single batched update for model + size so downstream effects
        // (availability, backgrounds) fire exactly once with correct values.
        if (updateDesignNoHistory) {
          updateDesignNoHistory(prev => ({
            ...prev,
            selectedModelId: selectedModel.id,
            selectedModelName: selectedModel.name,
            selectedSizeId: selectedSize.id,
            selectedSizeName: sizeName,
            porcelain_width_in: selectedSize.porcelain_width_in ?? prev.porcelain_width_in,
            porcelain_height_in: selectedSize.porcelain_height_in ?? prev.porcelain_height_in,
            porcelain_shape: selectedSize.porcelain_shape ?? prev.porcelain_shape,
          }));
        }

        if (setSelectedGraniteImage) {
          setSelectedGraniteImage(null);
        }

        try { await applyBaseForSelection(selectedModel.id, selectedSize.id, selectedColorId ?? null); } catch { }
        onShapeSelect(selectedModel.id, sizeName, selectedSize.id);
        setIsLoading(false);
        onClose();
      };

      const logical = parseSizeFromPath(selectedSize.preview_image || imgSrc);
      if (logical) {
        // Dimensions encoded in filename — no image download needed here.
        // Designer.tsx will load the image for actual rendering.
        applyDimensions(logical.w, logical.h);
      } else {
        // Fallback: load image to read natural pixel dimensions
        const img = new Image();
        img.onload = () => { applyDimensions(img.naturalWidth || img.width, img.naturalHeight || img.height); };
        img.onerror = () => { handleFallbackTemplate(colorId, selectedModel.id); };
        img.src = imgSrc;
      }
    } else {
      handleFallbackTemplate(colorId, selectedModel.id);
    }
  }, [
    selectedModel,
    sizes,
    selectedSizeIndex,
    previewUrl,
    selectedColorId,
    setIsLoading,
    setLoadingMessage,
    incrementTemplateResetKey,
    setTemplateElement,
    setSelectedGraniteMeta,
    setSelectedGraniteImage,
    updateDesignNoHistory,
    onShapeSelect,
    onClose,
    handleFallbackTemplate,
    setIsApplyingDesign,
    applyBaseForSelection
  ]);





  return (
    <div style={{
      padding: 20,
      maxWidth: 800,
      margin: '0 auto',
      background: '#fff',
      borderRadius: 8,
      position: 'relative'
    }}>
      {(modelsLoading || sizesLoading || previewLoading) && (
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(255, 255, 255, 0.7)',
          zIndex: 20,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 8
        }}>
          <CenterLoader style={{ padding: 0 }}>
            <LocalSpinner $size={32} />
            <span>Loading...</span>
          </CenterLoader>
        </div>
      )}

      <Breadcrumbs>
        {breadcrumbs.map((crumb, index) => (
          <React.Fragment key={index}>
            {index > 0 && <span className="sep">{'>'}</span>}
            {crumb.isCurrent ? (
              <span className="current">{crumb.label}</span>
            ) : (
              <span className="crumb" onClick={crumb.onClick}>{crumb.label}</span>
            )}
          </React.Fragment>
        ))}
      </Breadcrumbs>

      <StepContainer $isVisible={step === 'shape'}>
        {renderModelSection()}
        {!modelsLoading && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <ActionButton onClick={onClose}>
              Cancel
            </ActionButton>
          </div>
        )}
      </StepContainer>

      <StepContainer $isVisible={step === 'size'}>
        {false ? (
          null
        ) : (
          <>
            {renderSizeSection()}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <ActionButton onClick={() => setStep('shape')}>
                Back
              </ActionButton>
            </div>
          </>
        )}
      </StepContainer>

    </div>
  );
};

export default Shapes;